import {
    Multiselect,
    MultiselectProps,
    Select,
    SelectProps
} from '@cloudscape-design/components';
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalState } from '../../pages/context/GlobalStateContext';

// --- CORE TYPES ---
type ItemCategory = {
    idCategory: number;
    category: string;
    hasAltitudes: number;
    value: string; // Cloudscape value (string)
    label: string; // Cloudscape label
};

// --- PROPS INTERFACES ---

// When single = true
interface SelectCategorySingleProps {
    single: true; //just true
    value?: number; // Single ID
    width?: number;
    onSelect?: (selected: number | null) => void; // <--- CHANGED: Returns ID or null
    visible?: boolean;
    showZero?: boolean;
    hasAltitudes?: boolean;
    readonly?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
}

// When single = false (or undefined, as per union type)
interface SelectCategoryMultiProps {
    single?: false; // default is "false"
    value?: number[]; // Array of IDs
    width?: number;
    onSelect?: (selected: number[]) => void; // <--- CHANGED: Returns array of IDs
    visible?: boolean;
    showZero?: boolean;
    hasAltitudes?: boolean;
    readonly?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
}

export type SelectCategoryProps =
    | SelectCategorySingleProps
    | SelectCategoryMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectCategory: React.FC<SelectCategoryProps> = (props) => {
    const {
        single,
        value,
        width = 200,
        readonly,
        visible,
        hasAltitudes = false,
        showZero = false,
        tokenLimit = 3,
        hideTokens = false,
        onSelect
    } = props;

    const { data } = useGlobalState();
    if (!data || !data.categories) return <></>;

    // State for Single Selection (holds the selected object)
    const [selectedSingle, setSelectedSingle] = useState<ItemCategory | null>(null);

    // State for Multi Selection (holds the array of selected objects)
    const [selectedMulti, setSelectedMulti] = useState<ItemCategory[]>([]);


    // --- Mapped Categories (Options List) ---

    // Memoize the calculation of available options
    const mappedCategories: ItemCategory[] = useMemo(() => {
        let categories: ItemCategory[] = data.categories
            .filter((plan: ItemCategory) => !hasAltitudes || plan.hasAltitudes === 1)
            .map((plan: ItemCategory) => ({
                idCategory: plan.idCategory,
                category: plan.category,
                hasAltitudes: plan.hasAltitudes,
                value: plan.idCategory.toString(),
                label: plan.category
            }));

        if (showZero) {
            categories.unshift({
                idCategory: 0,
                category: "Seleccionar categoría",
                hasAltitudes: 0,
                value: "0",
                label: "Seleccionar categoría"
            });
        }
        return categories;
    }, [data.categories, hasAltitudes, showZero]);


    // --- Initialization & Value Sync Effect ---

    useEffect(() => {
        if (!value) {
            setSelectedSingle(null);
            setSelectedMulti([]);
            return;
        }

        if (single) {
            // SINGLE MODE: Value is a number (ID)
            const id = value as number;
            const match = mappedCategories.find(cat => cat.idCategory === id) || null;
            setSelectedSingle(match);

        } else {
            // MULTI MODE: Value is an array of numbers (IDs)
            const ids = value as number[];
            const matches = mappedCategories.filter(cat => ids.includes(cat.idCategory));
            setSelectedMulti(matches);
        }
    }, [value, single, mappedCategories]);


    // --- Single Select Handler ---
    const handleSingleChange: SelectProps['onChange'] = ({ detail }) => {
        const selectedOption = detail.selectedOption as ItemCategory | null;

        setSelectedSingle(selectedOption);

        if (onSelect && single) {
            const selectedId = selectedOption?.idCategory ?? null; // Extract ID
            // FIX: Assert the function signature directly to satisfy the compiler
            (onSelect as (selected: number | null) => void)(selectedId);
        }
    };

    // --- Multi Select Handler ---
    const handleMultiChange: MultiselectProps['onChange'] = ({ detail }) => {
        const list = detail.selectedOptions as ItemCategory[];

        // Use spread to ensure setting a new array reference
        setSelectedMulti([...list]);

        if (onSelect && !single) {
            const selectedIds = list.map(item => item.idCategory); // Extract array of IDs
            // FIX: Assert the function signature directly to satisfy the compiler
            (onSelect as (selected: number[]) => void)(selectedIds);
        }
    };

    // --- Rendering ---

    const placeholder = single ? "Seleccionar categoría" : "Seleccionar categorías";
    const wrapperStyle: React.CSSProperties = {
        width: width > 0 ? `${width}px` : '100%'
    };
    return (
        <div style={wrapperStyle}>
            {single ? (
                // SINGLE SELECTION
                <Select
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOption={selectedSingle}
                    options={mappedCategories}
                    onChange={handleSingleChange}
                />
            ) : (
                // MULTI SELECTION
                <Multiselect
                    tokenLimit={tokenLimit}
                    hideTokens={hideTokens}
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOptions={selectedMulti}
                    options={mappedCategories}
                    onChange={handleMultiChange}
                />
            )}

        </div>
    );
};

export default SelectCategory;